import { useState, type ReactNode } from 'react';
import { Button } from '../../components/Button.tsx';
import { Checkbox } from '../../components/Checkbox.tsx';
import { LetterDetails } from './LetterDetails.tsx';
import { date, type Letter } from './model.ts';
import styles from './authorization.module.css';

/** Mailbox proof never substitutes for explicit acceptance of the full letter. */
export function ApprovalReview({
  letter,
  purpose = 'approve',
  expiresAt,
  busy,
  onConfirm,
  alternateAction,
}: {
  letter: Letter;
  purpose?: 'approve' | 'manage';
  expiresAt: string;
  busy: boolean;
  onConfirm: () => void;
  alternateAction?: ReactNode;
}) {
  const [accepted, setAccepted] = useState(false);
  return (
    <div className={styles.approval}>
      <h2>{purpose === 'manage' ? 'Confirm owner access' : 'Confirm the exact mandate'}</h2>
      <p>
        This review expires {date(expiresAt)}. Verifying your email or opening this page does not
        approve anything.
      </p>
      <LetterDetails terms={letter.terms} />
      <p className={styles.notice}>
        Active testing execution, monitoring, and protection are governed by separate engagement
        mandates. Your approval records permission and provider policy. Traceira signs the record;
        it does not start a test.
      </p>
      <div className={styles.approvalActions}>
        <Checkbox isSelected={accepted} onChange={setAccepted}>
          {purpose === 'manage'
            ? 'I confirm access to the authorizer’s mailbox and want to manage this letter.'
            : 'I am the named authorizer, have authority over these assets, and approve these exact terms and their public scope summary.'}
        </Checkbox>
        <div className={styles.actions}>
          {alternateAction}
          <Button
            className={styles.reviewAction}
            variant="primary"
            onPress={onConfirm}
            isDisabled={!accepted || busy}
          >
            {busy
              ? 'Recording confirmation…'
              : purpose === 'manage'
                ? 'Confirm owner access'
                : 'Approve and sign authorization'}
          </Button>
        </div>
      </div>
    </div>
  );
}
